/* Copyright (c) 2020-present CloudIO Inc */

import { getName } from '../../registry/Manager';
import { FocusableItems } from 'hooks/useFocus';
import { Platform } from '../../platform/Platform';
import { ActionManager, ActionManagerFn } from '../../registry/Component.type';
import { baseActionManager, baseActionProps } from '../../registry/Components';
import { defaultValue } from './ActionFocus';

const ActionFocusManager: ActionManagerFn<'ActionFocus'> = (
  platform: Platform,
): ActionManager<'ActionFocus'> => {
  return {
    ...baseActionManager(platform, 'ActionFocus'),
    defaultValue,
    props: {
      ...baseActionProps,
      targetItemId: {
        label: 'Target Item ID',
        type: 'ItemID',
        validateItemId: (value, { pageId, appUid, itemId }) => {
          const item = platform.getItem(appUid, pageId, value);
          if (!(item && FocusableItems.includes(item.type))) {
            return `Item id '${value}' doesn't belong to a focus supported item. Supported items are ${FocusableItems.map(
              (t) => getName(t),
            ).join(', ')}`;
          }
          return '';
        },
      },
    },
    name: 'Focus Input Field',
    getDisplayText: (item) => item.props.targetItemId,
    icon: { iconName: 'bullseye-pointer' },
    description: `**Overview**<br/>
The Focus Input Field action is a user experience (UX) control component designed to programmatically move the browser's cursor focus to a specific input element within the application. It allows developers to guide the user's workflow by automatically selecting the next relevant field for data entry, rather than relying on the user to click manually.<br/><br/>
This component incorporates dynamic validation logic to ensure that the target element is actually capable of receiving focus. It relies on an imported list of FocusableItems to verify compatibility, preventing runtime issues where the application attempts to focus on non-interactive or unsupported elements.

**Key Features**<br/>
* **Workflow Guidance:** Explicit control over the input sequence, allowing the application to "jump" the cursor to specific fields based on logic or events.
* **Compatibility Validation:** Implements strict validation via validateItemId. It checks if the target item exists and if its component type is included in the supported FocusableItems list.
* **Error Feedback:** If an invalid target is selected, the component returns a detailed error message listing the readable names of all currently supported focusable component types.
* **Registry Visibility:** The getDisplayText method displays the targetItemId directly in the action list, making it easy to see which field is being targeted.

**Properties**<br/>
* **Target Item ID (targetItemId):** An ItemID property that identifies the specific UI component to receive focus.
  * *Validation:* Checks if the item is one of the supported focusable types (e.g., Text Field).
  * *Error Message:* "Item id '[value]' doesn't belong to a focus supported item. Supported items are [List]..."

**Use Cases**<br/>
* **Form Navigation:** Automatically focusing the "Password" field immediately after the user enters their "Username" and presses Enter.
* **Error Correction:** Moving the cursor back to a specific input field that failed validation so the user can immediately correct the mistake.
* **Reset Workflow:** Setting focus to the first field of a form after a "Clear" or "Submit" action to prepare for the next entry.`,
  };
};

export default ActionFocusManager;
